import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { ArrowLeft, Users, DollarSign, BookOpen, TrendingUp, Calendar, Download } from "lucide-react";
import { exportToCSV, formatReportData } from "@/lib/export-utils";

interface ReportData {
  totalUsers: number;
  activeUsers: number;
  totalRevenue: number;
  courseCompletions: number;
  activeSessions: number;
  supportTickets: number;
}

const AdminReports = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [reportData, setReportData] = useState<ReportData>({
    totalUsers: 0,
    activeUsers: 0,
    totalRevenue: 0,
    courseCompletions: 0,
    activeSessions: 0,
    supportTickets: 0,
  });
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    checkAdminAccess();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      generateReport();
    }
  }, [isAdmin, selectedMonth, selectedYear]);

  const checkAdminAccess = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      navigate("/auth");
      return;
    }

    const { data: roleData } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", session.user.id)
      .eq("role", "admin")
      .single();

    if (!roleData) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to access this page.",
        variant: "destructive",
      });
      navigate("/dashboard");
      return;
    }

    setIsAdmin(true);
  };

  const generateReport = async () => {
    setLoading(true);
    
    const startDate = new Date(selectedYear, selectedMonth, 1);
    const endDate = new Date(selectedYear, selectedMonth + 1, 0);

    const [
      usersData,
      paymentsData,
      progressData,
      sessionsData,
      ticketsData
    ] = await Promise.all([
      supabase.from("profiles").select("id, created_at"),
      supabase.from("user_payments").select("amount").eq("status", "completed"),
      supabase.from("course_progress").select("*").eq("completed", true),
      supabase.from("live_sessions").select("*").eq("status", "scheduled"),
      supabase.from("support_tickets").select("*")
    ]);

    const monthlyUsers = usersData.data?.filter(u => {
      const created = new Date(u.created_at);
      return created >= startDate && created <= endDate;
    }).length || 0;

    const monthlyRevenue = paymentsData.data?.reduce((sum, p) => sum + (p.amount || 0), 0) || 0;

    setReportData({
      totalUsers: usersData.data?.length || 0,
      activeUsers: monthlyUsers,
      totalRevenue: monthlyRevenue / 100,
      courseCompletions: progressData.data?.length || 0,
      activeSessions: sessionsData.data?.length || 0,
      supportTickets: ticketsData.data?.length || 0,
    });

    setLoading(false);
  };

  const handleDownloadReport = () => {
    const formattedData = formatReportData(reportData);
    const filename = `report_${months[selectedMonth]}_${selectedYear}`;
    exportToCSV(formattedData, filename);
    
    toast({
      title: "Report Downloaded",
      description: "Your report has been downloaded successfully.",
    });
  };

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  if (!isAdmin) return null;

  return (
    <div className="container mx-auto p-6">
      <Button
        variant="ghost"
        onClick={() => navigate("/dashboard")}
        className="mb-6"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Dashboard
      </Button>

      <div className="mb-6">
        <h1 className="text-3xl font-bold">Monthly Reports & Analytics</h1>
        <p className="text-muted-foreground">Platform usage and performance metrics</p>
      </div>

      <div className="flex gap-4 mb-6">
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(Number(e.target.value))}
          className="px-4 py-2 border rounded-lg"
        >
          {months.map((month, index) => (
            <option key={month} value={index}>{month}</option>
          ))}
        </select>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          className="px-4 py-2 border rounded-lg"
        >
          {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
        <Button onClick={generateReport}>Generate Report</Button>
        <Button onClick={handleDownloadReport} variant="outline" disabled={loading}>
          <Download className="mr-2 h-4 w-4" />
          Download Report
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{reportData.totalUsers}</div>
              <p className="text-xs text-muted-foreground">
                +{reportData.activeUsers} this month
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${reportData.totalRevenue.toFixed(2)}</div>
              <p className="text-xs text-muted-foreground">Total platform revenue</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Course Completions</CardTitle>
              <BookOpen className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{reportData.courseCompletions}</div>
              <p className="text-xs text-muted-foreground">Students finished courses</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Sessions</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{reportData.activeSessions}</div>
              <p className="text-xs text-muted-foreground">Scheduled live sessions</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Support Tickets</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{reportData.supportTickets}</div>
              <p className="text-xs text-muted-foreground">Total support requests</p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminReports;
